// Enhanced ChatSidebar with FIXED complete file detection
import React, {useState, useRef, useEffect, useCallback} from 'react';
import {Principal} from '@dfinity/principal';
import ClaudeChatService from "./claude/ClaudeChatService";
import useIDEStore from './zustand/useIDEStore';

interface ChatMessage {
    id: string;
    role: 'user' | 'assistant';
    content: string;
    timestamp: number;
    fileContext?: string[];
    hasFileChanges?: boolean;
    extractedFiles?: { [key: string]: string };
}

interface ProjectFile {
    name: string;
    content: string;
    fullId?: string;
    path?: string;
    fileName?: string;
    id?: string;
}

interface ChatSidebarProps {
    isExpanded: boolean;
    onToggle: () => void;
    activeFile: string;
    fileContent: string;
    files: ProjectFile[];
    project: any;
    onUpdateContent: (content: string) => void;
    onError: (error: string) => void;
    onSuccess: (message: string) => void;
    systemCanisters: any;
    userPrincipal: Principal;
    onFileSystemChange?: () => void;
}

interface AIRule {
    id: string;
    name: string;
    description: string;
    content: string;
    type: 'custom' | 'url' | 'mcp' | 'template' | 'github';
    enabled: boolean;
    category: 'security' | 'style' | 'performance' | 'architecture' | 'general';
    url?: string;
    mcpServer?: string;
    lastUpdated?: number;
    status?: 'active' | 'loading' | 'error';
    errorMessage?: string;
    githubUrl?: string;
    githubPath?: string;
    githubBranch?: string;
    extractionType?: 'readme' | 'contributing' | 'docs' | 'examples' | 'all';
}

interface GitHubSource {
    id: string;
    name: string;
    githubUrl: string;
    githubPath?: string;
    githubBranch?: string;
    extractionType: 'readme' | 'contributing' | 'docs' | 'examples' | 'all';
    category: 'security' | 'style' | 'performance' | 'architecture' | 'general';
    description: string;
    status: 'active' | 'loading' | 'error';
    lastFetched?: number;
    contentPreview?: string;
    errorMessage?: string;
}

interface DocumentationSource {
    id: string;
    name: string;
    url: string;
    status: 'active' | 'loading' | 'error';
    contentPreview?: string;
}

interface MCPServer {
    id: string;
    name: string;
    url: string;
    status: 'connected' | 'connecting' | 'error' | 'disconnected';
    availableTools?: string[];
}

const ChatSidebar = React.forwardRef<any, ChatSidebarProps>(({
                                                                 isExpanded,
                                                                 onToggle,
                                                                 activeFile,
                                                                 fileContent,
                                                                 files,
                                                                 project,
                                                                 onUpdateContent,
                                                                 onError,
                                                                 onSuccess,
                                                                 systemCanisters,
                                                                 userPrincipal,
                                                                 onFileSystemChange
                                                             }, ref) => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState<string[]>(
        files.filter(file => file.name && file.name.trim().length > 0).map(file => file.name)
    );
    const [showFileSelector, setShowFileSelector] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const [streamingContent, setStreamingContent] = useState('');
    const [isApplyingChanges, setIsApplyingChanges] = useState(false);
    const [applyProgress, setApplyProgress] = useState({progress: 0, message: ''});

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const chatService = useRef(new ClaudeChatService());

    // Get AI context from IDE store and localStorage
    const aiRules = useIDEStore(state => state.aiRules || []);
    const [docSources, setDocSources] = useState<DocumentationSource[]>([]);
    const [mcpServers, setMcpServers] = useState<MCPServer[]>([]);
    const [githubSources, setGithubSources] = useState<GitHubSource[]>([]);

    const [noCodeMode, setNoCodeMode] = useState(false);

    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [autoScroll, setAutoScroll] = useState(true);




    // Load documentation sources and MCP servers from localStorage
    useEffect(() => {
        try {
            const savedDocs = localStorage.getItem('ide-doc-sources');
            const savedMcp = localStorage.getItem('ide-mcp-servers');

            if (savedDocs) {
                setDocSources(JSON.parse(savedDocs));
            }
            if (savedMcp) {
                setMcpServers(JSON.parse(savedMcp));
            }
        } catch (error) {
            console.warn('Failed to load persisted context data:', error);
        }
    }, []);

    useEffect(() => {
        try {
            const savedGitHub = localStorage.getItem('ide-github-sources');
            if (savedGitHub) {
                setGithubSources(JSON.parse(savedGitHub));
            }
        } catch (error) {
            console.warn('Failed to load GitHub sources:', error);
        }
    }, []);

    const scrollToBottom = () => {
        if (autoScroll) {
            messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages, streamingContent]);

    const adjustTextareaHeight = useCallback(() => {
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
            inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 120) + 'px';
        }
    }, []);

    useEffect(() => {
        adjustTextareaHeight();
    }, [inputValue, adjustTextareaHeight]);

    const getCompleteFileContent = useCallback(async (fileName: string): Promise<string> => {
        console.log(`📖 CHAT: Getting complete content for file: ${fileName}`);

        if (fileName === activeFile) {
            return fileContent;
        }

        const fileObj = files.find(f => f.name === fileName);
        if (fileObj && fileObj.content && fileObj.content.trim().length > 0) {
            return fileObj.content;
        }

        if (fileObj && systemCanisters.userActor && userPrincipal && project?.id) {
            try {
                const result = await systemCanisters.userActor.getCodeArtifactContent(
                    userPrincipal, project.id, fileName, []
                );

                if ('ok' in result) {
                    const freshContent = 'Text' in result.ok ? result.ok.Text : '';
                    return freshContent;
                }
            } catch (error) {
                console.error(`❌ CHAT: Error loading fresh content for ${fileName}:`, error);
            }
        }

        return fileObj?.content || `// Content not available for ${fileName}`;
    }, [activeFile, fileContent, files, systemCanisters.userActor, userPrincipal, project?.id]);

    const stopResponse = () => {
        console.log('🛑 STOP BUTTON CLICKED');
        console.log('🛑 Current abortController:', abortController);

        if (abortController) {
            console.log('🛑 Aborting controller...');
            abortController.abort();
            setAbortController(null);
        }

        console.log('🛑 Setting streaming states to false...');
        setIsStreaming(false);
        setIsLoading(false);
        setStreamingContent('');
        console.log('🛑 Stop response complete');
    };

    // Enhanced context building with all sources
    const buildComprehensiveContext = useCallback(async () => {
        const filesToInclude = selectedFiles.length > 0
            ? [...new Set([activeFile, ...selectedFiles])]
            : [];

        console.log(`🔍 CHAT: Building comprehensive context for ${filesToInclude.length} files`);

        const selectedFileContents: { [fileName: string]: string } = {};

        for (const fileName of filesToInclude) {
            if (fileName) {
                try {
                    const content = await getCompleteFileContent(fileName);
                    selectedFileContents[fileName] = content;
                } catch (error) {
                    console.error(`❌ CHAT: Failed to load content for ${fileName}:`, error);
                    selectedFileContents[fileName] = `// Error loading content for ${fileName}`;
                }
            }
        }

        // Get enhanced AI context including all sources
        const enhancedAIContext = getEnhancedAIContext();

        const context = {
            activeFile,
            fileContent,
            selectedFiles: filesToInclude,
            selectedFileContents,
            fileContents: selectedFileContents,
            projectStructure: files.map(f => ({
                name: f.name,
                type: f.name.split('.').pop() || 'unknown'
            })),
            projectInfo: {
                id: project?.id || '',
                name: project?.name || '',
                type: project?.projectType || 'unknown'
            },
            aiRulesContext: enhancedAIContext
        };

        console.log(`📁 CHAT: Enhanced context built with ${Object.keys(selectedFileContents).length} files and comprehensive AI context`);
        return context;
    }, [activeFile, fileContent, selectedFiles, files, project, getCompleteFileContent]);

    const getEnhancedAIContext = (): string => {
        let context = '';

        // 1. Active AI Rules
        const enabledRules = aiRules.filter(rule => rule.enabled && rule.status === 'active');
        if (enabledRules.length > 0) {
            const rulesByCategory = enabledRules.reduce((acc, rule) => {
                if (!acc[rule.category]) acc[rule.category] = [];
                acc[rule.category].push(rule);
                return acc;
            }, {} as Record<string, AIRule[]>);

            context += '\n\n=== AI ASSISTANT RULES ===\n';
            context += 'Please follow these specific guidelines when providing assistance:\n\n';

            Object.entries(rulesByCategory).forEach(([category, rules]) => {
                context += `## ${category.toUpperCase()} GUIDELINES:\n`;
                rules.forEach(rule => {
                    context += `- ${rule.name}: ${rule.content}\n`;
                });
                context += '\n';
            });

            context += '=== END RULES ===\n\n';
        }

        // 2. Documentation Sources
        const activeDocs = docSources.filter(doc => doc.status === 'active' && doc.contentPreview);
        if (activeDocs.length > 0) {
            context += '\n\n=== DOCUMENTATION CONTEXT ===\n';
            context += 'Reference these documentation sources when providing assistance:\n\n';

            activeDocs.forEach(doc => {
                context += `## ${doc.name.toUpperCase()}\n`;
                context += `Source: ${doc.url}\n`;
                context += `Content: ${doc.contentPreview}\n\n`;
            });

            context += '=== END DOCUMENTATION ===\n\n';
        }

        // 3. GitHub Sources
        const activeGitHub = githubSources.filter(github => github.status === 'active' && github.contentPreview);
        if (activeGitHub.length > 0) {
            context += '\n\n=== GITHUB REPOSITORY CONTEXT ===\n';
            context += 'Reference these GitHub repository guidelines when providing assistance:\n\n';

            activeGitHub.forEach(github => {
                context += `## ${github.name.toUpperCase()}\n`;
                context += `Repository: ${github.githubUrl}\n`;
                context += `Extraction Type: ${github.extractionType}\n`;
                context += `Category: ${github.category}\n`;
                context += `Guidelines: ${github.contentPreview}\n\n`;
            });

            context += '=== END GITHUB CONTEXT ===\n\n';
        }

        // 4. MCP Server Capabilities
        const connectedMcp = mcpServers.filter(server => server.status === 'connected');
        if (connectedMcp.length > 0) {
            context += '\n\n=== MCP CAPABILITIES ===\n';
            context += 'Available MCP servers and their tools:\n\n';

            connectedMcp.forEach(server => {
                context += `## ${server.name.toUpperCase()}\n`;
                context += `Server: ${server.url}\n`;
                if (server.availableTools && server.availableTools.length > 0) {
                    context += `Available Tools: ${server.availableTools.join(', ')}\n`;
                }
                context += 'Note: You can leverage these MCP capabilities for enhanced functionality.\n\n';
            });

            context += '=== END MCP CAPABILITIES ===\n\n';
        }

        // 5. Context Summary
        if (enabledRules.length > 0 || activeDocs.length > 0 || activeGitHub.length > 0 || connectedMcp.length > 0) {
            context += '\n\n=== CONTEXT SUMMARY ===\n';
            context += `Active Elements:\n`;
            if (enabledRules.length > 0) context += `- ${enabledRules.length} AI rules enforced\n`;
            if (activeDocs.length > 0) context += `- ${activeDocs.length} web documentation sources available\n`;
            if (activeGitHub.length > 0) context += `- ${activeGitHub.length} GitHub repository sources available\n`;
            if (connectedMcp.length > 0) context += `- ${connectedMcp.length} MCP servers connected\n`;
            context += '\nPlease consider all these sources when providing assistance.\n';
            context += '=== END CONTEXT SUMMARY ===\n\n';
        }

        return context;
    };

    const getApiKey = async (): Promise<string> => {
        // Get API key from localStorage
        const localStorageKey = localStorage.getItem('claude_api_key');
        if (localStorageKey && localStorageKey.trim()) {
            return localStorageKey.trim();
        }

        // If no API key found, throw error
        throw new Error('Claude API key not found. Please set your API key in the project settings.');

        /* PLATFORM CANISTER METHOD - COMMENTED OUT FOR FUTURE USE
        if (!systemCanisters.platformActor) {
            throw new Error('Platform canister not initialized');
        }

        try {
            const result = await systemCanisters.platformActor.getClaudeApiKey();
            if ('ok' in result) {
                return result.ok;
            } else {
                throw new Error(result.err || 'Failed to retrieve Claude API key');
            }
        } catch (error) {
            console.error('Error fetching API key from platform:', error);
            throw new Error(`Claude API not configured: ${error instanceof Error ? error.message : String(error)}`);
        }
        */
    };

// FIXED: applyFileChanges function - prevents nested project directory creation
    const applyFileChanges = async (extractedFiles: { [key: string]: string }) => {
        if (!systemCanisters.userActor || !userPrincipal || !project?.id) {
            throw new Error('Required dependencies not available for file operations');
        }

        setIsApplyingChanges(true);
        setApplyProgress({progress: 10, message: 'Analyzing file changes...'});

        try {
            console.log(`🔧 CHAT: Applying ${Object.keys(extractedFiles).length} file changes`);

            let uploadedCount = 0;
            let updatedCount = 0;
            const totalFiles = Object.keys(extractedFiles).length;

            for (const [fileName, fileContent] of Object.entries(extractedFiles)) {
                if (!fileContent || typeof fileContent !== 'string' || !fileContent.trim()) {
                    continue;
                }

                let cleanFileName = fileName;
                cleanFileName = cleanFileName.replace(/^.*?Complete file:\s*/i, '');
                cleanFileName = cleanFileName.replace(/^.*?\/\/\s*/, '');
                cleanFileName = cleanFileName.replace(/^.*?\/\*.*?\*\/\s*/s, '');
                cleanFileName = cleanFileName.trim();

                const extension = cleanFileName.substring(cleanFileName.lastIndexOf('.')).toLowerCase();

                if (!['.mo', '.tsx', '.ts', '.css', '.js', '.jsx', '.json'].includes(extension)) {
                    continue;
                }

                let filePath = '';
                let fileType = '';
                let languageType = '';
                let finalFileName = cleanFileName;

                // FIXED: Handle file paths correctly without creating nested project directories
                if (cleanFileName.includes('/')) {
                    const pathParts = cleanFileName.split('/');
                    finalFileName = pathParts.pop() || cleanFileName;

                    // IMPORTANT: Don't prepend project name - use the path as-is
                    filePath = pathParts.join('/');

                    // Only add project name if the path doesn't already start with the project structure
                    if (!filePath.startsWith('src/') && !filePath.startsWith(project.name)) {
                        filePath = `${project.name}/${filePath}`;
                    } else if (filePath.startsWith('src/')) {
                        // Path already starts with src/, so just prepend project name
                        filePath = `${project.name}/${filePath}`;
                    }
                } else {
                    // File without path - use default locations
                    if (['.tsx', '.ts', '.css', '.js', '.jsx'].includes(extension)) {
                        filePath = `${project.name}/src/frontend/src`;
                    } else if (extension === '.mo') {
                        filePath = `${project.name}/src/backend/src`;
                    } else if (['.json'].includes(extension)) {
                        // Config files go in frontend root
                        filePath = `${project.name}/src/frontend`;
                    } else {
                        filePath = project.name;
                    }
                }

                // Set file type and language type
                if (extension === '.css') {
                    fileType = 'text/css';
                    languageType = 'css';
                } else if (['.tsx', '.ts', '.js', '.jsx'].includes(extension)) {
                    fileType = 'text/typescript';
                    languageType = 'typescript';
                } else if (extension === '.mo') {
                    fileType = 'text/x-motoko';
                    languageType = 'motoko';
                } else if (extension === '.json') {
                    fileType = 'application/json';
                    languageType = 'json';
                }

                console.log(`📁 PROCESSING FILE:`, {
                    originalPath: fileName,
                    cleanFileName: cleanFileName,
                    finalFileName: finalFileName,
                    filePath: filePath,
                    fullPath: `${filePath}/${finalFileName}`
                });

                try {
                    const createResult = await systemCanisters.userActor.createCodeArtifact(
                        userPrincipal,
                        project.id,
                        finalFileName,
                        {Text: fileContent},
                        fileType,
                        languageType,
                        filePath,
                        []
                    );

                    if ('ok' in createResult) {
                        uploadedCount++;
                        console.log(`✅ CREATED: ${filePath}/${finalFileName}`);
                    } else {
                        const updateResult = await systemCanisters.userActor.updateCodeArtifact(
                            userPrincipal,
                            project.id,
                            finalFileName,
                            {Text: fileContent},
                            filePath,
                            []
                        );

                        if ('ok' in updateResult) {
                            updatedCount++;
                            console.log(`✅ UPDATED: ${filePath}/${finalFileName}`);
                        }
                    }
                } catch (error) {
                    console.error(`💥 CHAT: Error processing ${finalFileName}:`, error);
                }

                const processedCount = uploadedCount + updatedCount;
                const fileProgress = 30 + ((processedCount / totalFiles) * 60);
                setApplyProgress({
                    progress: Math.round(fileProgress),
                    message: `Processed ${processedCount}/${totalFiles} files...`
                });
            }

            setApplyProgress({progress: 95, message: 'Refreshing file system...'});

            if (onFileSystemChange) {
                onFileSystemChange();
            }

            const totalProcessed = uploadedCount + updatedCount;
            setApplyProgress({
                progress: 100,
                message: `✅ Applied changes to ${totalProcessed} files! (${uploadedCount} created, ${updatedCount} updated)`
            });

            onSuccess(`Successfully applied changes: ${uploadedCount} files created, ${updatedCount} files updated`);

            setTimeout(() => {
                setIsApplyingChanges(false);
                setApplyProgress({progress: 0, message: ''});
            }, 3000);

        } catch (error) {
            console.error('CHAT: Error applying file changes:', error);
            const errorMessage = error instanceof Error ? error.message : String(error);
            setApplyProgress({progress: 0, message: `Error: ${errorMessage}`});
            onError(`Failed to apply changes: ${errorMessage}`);

            setTimeout(() => {
                setIsApplyingChanges(false);
                setApplyProgress({progress: 0, message: ''});
            }, 5000);
        }
    };

    const sendMessage = async (messageText?: string) => {
        let textToSend = messageText || inputValue.trim();
        if (!textToSend || isLoading) return;

        const controller = new AbortController();
        setAbortController(controller);


        if (noCodeMode && !textToSend.toLowerCase().includes('no code')) {
            textToSend += '\n\nPlease provide explanations and guidance without generating any code blocks or file modifications.';
        }


        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: textToSend,
            timestamp: Date.now(),
            fileContext: selectedFiles.length > 0 ? selectedFiles : [activeFile]
        };

        const cleanMessages = messages.filter(msg => msg.content && msg.content.trim().length > 0);
        const newMessages = [...cleanMessages, userMessage];
        setMessages(newMessages);
        setInputValue('');
        setIsLoading(true);
        setIsStreaming(true);
        setStreamingContent('');

        try {
            const apiKey = await getApiKey();
            const context = await buildComprehensiveContext();

            // Enhanced prompt with comprehensive AI context
            const enhancedAIContext = getEnhancedAIContext();
            const enhancedPrompt = `${textToSend}

${enhancedAIContext}

When providing code solutions, please provide COMPLETE file content when modifying existing files. Use this format for each file:

\`\`\`typescript
// Complete file: src/frontend/src/FileName.tsx
[COMPLETE FILE CONTENT WITH ALL IMPORTS, FUNCTIONS, AND MODIFICATIONS]
\`\`\`

For any code changes, include the entire file content, not just snippets.`;

            console.log(`💬 CHAT: Sending request with enhanced AI context`);
            console.log(`📁 CONTEXT: ${Object.keys(context.fileContents || {}).length} complete files`);
            console.log(`🎯 AI RULES: ${aiRules.filter(r => r.enabled).length} active`);
            console.log(`📚 DOCS: ${docSources.filter(d => d.status === 'active').length} sources`);
            console.log(`🔌 MCP: ${mcpServers.filter(m => m.status === 'connected').length} connected`);

            const finalMessages = [...cleanMessages, {...userMessage, content: enhancedPrompt}];

            const hasEmptyMessages = finalMessages.some(msg => !msg.content || msg.content.trim().length === 0);
            if (hasEmptyMessages) {
                throw new Error('Cannot send request: Empty messages detected in conversation');
            }


            await chatService.current.sendStreamingChatWithFileExtraction(
                apiKey,
                finalMessages,
                context,
                true,
                (chunk: string) => {
                    // Use the controller variable directly, not the state
                    if (controller.signal.aborted) {
                        console.log('🛑 Chunk rejected - aborted');
                        return;
                    }
                    setStreamingContent(prev => prev + chunk);
                },
                async (finalResponse: string, files: { [key: string]: string } = {}) => {
                    // Use the controller variable directly, not the state
                    if (controller.signal.aborted) {
                        console.log('🛑 Completion rejected - aborted');
                        return;
                    }

                    console.log('🎯 STREAMING COMPLETION CALLBACK TRIGGERED');
                    console.log(`✅ CHAT: Response complete (${finalResponse.length} chars, ${Object.keys(files).length} files)`);

                    if (finalResponse && finalResponse.trim().length > 0) {
                        const assistantMessage: ChatMessage = {
                            id: (Date.now() + 1).toString(),
                            role: 'assistant',
                            content: finalResponse.trim(),
                            timestamp: Date.now(),
                            hasFileChanges: Object.keys(files).length > 0,
                            extractedFiles: files
                        };

                        const finalMessagesWithResponse = [...newMessages, assistantMessage];
                        setMessages(finalMessagesWithResponse);
                    } else {
                        onError('Received empty response from AI assistant');
                    }

                    setStreamingContent('');
                    setIsStreaming(false);
                    setAbortController(null);
                }
            );

        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            onError(`Chat error: ${errorMessage}`);

            const errorContent = `Sorry, I encountered an error: ${errorMessage}`;
            if (errorContent.trim().length > 0) {
                const errorMsg: ChatMessage = {
                    id: (Date.now() + 1).toString(),
                    role: 'assistant',
                    content: errorContent,
                    timestamp: Date.now()
                };
                setMessages(prev => [...prev.filter(msg => msg.content && msg.content.trim().length > 0), errorMsg]);
            }

            setStreamingContent('');
            setIsStreaming(false);
        } finally {
            setIsLoading(false);
        }
    };


    const sendFixMessage = useCallback((errorMessage: string) => {
        if (!errorMessage) return;

        console.log('🔧 ChatSidebar: Received fix request for error:', errorMessage.substring(0, 100) + '...');

        // Disable no-code mode to ensure we get an actual fix
        setNoCodeMode(false);

        // Create the fix prompt
        const fixPrompt = `Please fix the following error that happened on last deploy:

${errorMessage}

Please analyze the error and provide a complete solution with updated code files to resolve this deployment issue.`;

        // Send the message directly using the existing sendMessage function
        sendMessage(fixPrompt);
    }, [sendMessage, setNoCodeMode]);


    React.useImperativeHandle(ref, () => ({
        sendFixMessage
    }), [sendFixMessage]);


    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const clearConversation = () => {
        setMessages([]);
        setStreamingContent('');
        console.log('🧹 CHAT: Conversation cleared');
    };


    const renderNoCodeToggle = () => {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 8px',
                backgroundColor: noCodeMode ? '#e8f5e9' : '#fff3cd',
                border: `1px solid ${noCodeMode ? '#4caf50' : '#ffc107'}`,
                borderRadius: '4px',
                fontSize: '10px',
                fontWeight: '500'
            }}>
            <span style={{color: noCodeMode ? '#2e7d32' : '#856404'}}>
                💬
            </span>
                <button
                    onClick={() => setNoCodeMode(!noCodeMode)}
                    style={{
                        width: '32px',
                        height: '16px',
                        borderRadius: '8px',
                        border: 'none',
                        backgroundColor: noCodeMode ? '#4caf50' : '#ccc',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <div style={{
                        width: '12px',
                        height: '12px',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        position: 'absolute',
                        top: '2px',
                        left: noCodeMode ? '18px' : '2px',
                        transition: 'all 0.2s ease',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.3)'
                    }}/>
                </button>
                <span style={{
                    fontSize: '9px',
                    color: noCodeMode ? '#2e7d32' : '#856404',
                    fontWeight: '600'
                }}>
                {noCodeMode ? 'No Code' : 'Code OK'}
            </span>
            </div>
        );
    };


    const renderFileSelector = () => {
        if (!showFileSelector) return null;

        const orderedFiles = [...files].sort((a, b) => {
            const extA = a.name.split('.').pop()?.toLowerCase() || '';
            const extB = b.name.split('.').pop()?.toLowerCase() || '';

            const extensionOrder: { [key: string]: number } = {
                'mo': 1, 'tsx': 2, 'ts': 3, 'js': 4, 'jsx': 5,
                'css': 6, 'scss': 7, 'json': 8, 'md': 9, 'txt': 10,
            };

            const orderA = extensionOrder[extA] || 999;
            const orderB = extensionOrder[extB] || 999;

            if (orderA !== orderB) return orderA - orderB;
            return a.name.localeCompare(b.name);
        });

        const allFilePaths = files.filter(file => file.name && file.name.trim().length > 0).map(file => file.name);
        const selectedCount = selectedFiles.length;
        const totalCount = allFilePaths.length;

        return (
            <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                backgroundColor: '#fdf6e3',
                border: '1px solid #8B4513',
                borderTop: 'none',
                maxHeight: '300px',
                overflowY: 'auto',
                zIndex: 1000,
                boxShadow: '0 4px 8px rgba(139, 69, 19, 0.2)'
            }}>
                <div style={{
                    padding: '12px',
                    backgroundColor: '#f3e5d8',
                    borderBottom: '1px solid rgba(139, 69, 19, 0.2)',
                    position: 'sticky',
                    top: 0
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '8px'
                    }}>
                        <span style={{
                            fontSize: '12px',
                            fontWeight: '600',
                            color: '#5C4033'
                        }}>
                            Select Files for Context
                        </span>
                        <span style={{
                            fontSize: '10px',
                            color: '#8B4513',
                            backgroundColor: 'rgba(139, 69, 19, 0.1)',
                            padding: '2px 6px',
                            borderRadius: '3px'
                        }}>
                            {selectedCount} of {totalCount} selected
                        </span>
                    </div>

                    <div style={{display: 'flex', gap: '8px'}}>
                        <button
                            onClick={() => setSelectedFiles(allFilePaths)}
                            disabled={selectedCount === totalCount}
                            style={{
                                flex: 1,
                                padding: '6px 12px',
                                backgroundColor: selectedCount === totalCount ? '#9B5523' : '#4B5320',
                                color: '#FFF5EE',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '11px',
                                cursor: selectedCount === totalCount ? 'not-allowed' : 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            ✓ Select All
                        </button>

                        <button
                            onClick={() => setSelectedFiles([])}
                            disabled={selectedCount === 0}
                            style={{
                                flex: 1,
                                padding: '6px 12px',
                                backgroundColor: selectedCount === 0 ? '#9B5523' : '#8B4513',
                                color: '#FFF5EE',
                                border: 'none',
                                borderRadius: '4px',
                                fontSize: '11px',
                                cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            ✗ Clear All
                        </button>
                    </div>
                </div>

                <div style={{padding: '8px'}}>
                    {orderedFiles.map(file => {
                        const isSelected = selectedFiles.includes(file.name);
                        const extension = file.name.split('.').pop()?.toLowerCase() || '';

                        return (
                            <div
                                key={file.name}
                                onClick={() => {
                                    setSelectedFiles(prev =>
                                        prev.includes(file.name)
                                            ? prev.filter(f => f !== file.name)
                                            : [...prev, file.name]
                                    );
                                }}
                                style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#e8d5c4' : 'transparent',
                                    borderRadius: '4px',
                                    marginBottom: '2px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    transition: 'background-color 0.2s ease'
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                    }}
                                    style={{margin: 0, accentColor: '#5C4033'}}
                                />
                                <div style={{flex: 1}}>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#5C4033',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}>
                                        <span style={{
                                            fontSize: '9px',
                                            color: '#8B4513',
                                            backgroundColor: 'rgba(139, 69, 19, 0.1)',
                                            padding: '1px 4px',
                                            borderRadius: '2px'
                                        }}>
                                            .{extension}
                                        </span>
                                        {file.name.split('/').pop()}
                                    </div>
                                    <div style={{
                                        fontSize: '10px',
                                        color: '#8B4513',
                                        marginTop: '2px'
                                    }}>
                                        {file.name.includes('/')
                                            ? file.name.substring(0, file.name.lastIndexOf('/'))
                                            : 'Root'
                                        }
                                    </div>
                                </div>
                                {isSelected && (
                                    <span style={{
                                        fontSize: '10px',
                                        color: '#4B5320',
                                        fontWeight: '600',
                                        backgroundColor: 'rgba(75, 83, 32, 0.15)',
                                        padding: '3px 6px',
                                        borderRadius: '4px'
                                    }}>
                                        ✓
                                    </span>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderApplyProgress = () => {
        if (!isApplyingChanges) return null;

        return (
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000
            }}>
                <div style={{
                    backgroundColor: '#fdf6e3',
                    padding: '24px',
                    borderRadius: '12px',
                    border: '2px solid #8B4513',
                    minWidth: '300px',
                    textAlign: 'center'
                }}>
                    <h3 style={{
                        margin: '0 0 16px 0',
                        color: '#5C4033',
                        fontSize: '16px'
                    }}>
                        Applying Changes
                    </h3>

                    <div style={{
                        width: '100%',
                        height: '8px',
                        backgroundColor: 'rgba(139, 69, 19, 0.2)',
                        borderRadius: '4px',
                        marginBottom: '12px',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            width: `${applyProgress.progress}%`,
                            height: '100%',
                            backgroundColor: '#4B5320',
                            borderRadius: '4px',
                            transition: 'width 0.3s ease'
                        }}/>
                    </div>

                    <p style={{
                        margin: 0,
                        color: '#8B4513',
                        fontSize: '14px'
                    }}>
                        {applyProgress.message}
                    </p>
                </div>
            </div>
        );
    };

    // FIXED: Enhanced complete file detection with better regex patterns
    const detectCompleteFiles = (content: string): { [key: string]: string } => {
        const files: { [key: string]: string } = {};

        console.log('🔍 DETECTING COMPLETE FILES in content:', content.substring(0, 200) + '...');

        // Enhanced patterns that match the actual format from the AI response
        const patterns = [
            // TypeScript/JavaScript files with various comment formats
            {
                regex: /```(?:typescript|ts|javascript|js|tsx|jsx)\s*\n(?:\/\/\s*Complete file:\s*([^\n]+\.(?:tsx?|jsx?|js))[^\n]*\n)?([\s\S]*?)```/gi,
                type: 'typescript'
            },
            // CSS files
            {
                regex: /```css\s*\n(?:\/\*\s*Complete file:\s*([^\n*]+\.css)[^\n*]*\s*\*\/\s*\n)?([\s\S]*?)```/gi,
                type: 'css'
            },
            // Motoko files
            {
                regex: /```motoko\s*\n(?:\/\/\s*Complete file:\s*([^\n]+\.mo)[^\n]*\n)?([\s\S]*?)```/gi,
                type: 'motoko'
            },
            // Generic pattern for any language with "Complete file:" comment
            {
                regex: /```\w*\s*\n(?:(?:\/\/|\/\*)\s*Complete file:\s*([^\n\*]+)(?:\*\/)?\s*\n)([\s\S]*?)```/gi,
                type: 'generic'
            },
            // Pattern for files without explicit "Complete file:" but with file paths in comments
            {
                regex: /```(?:typescript|ts|tsx|jsx|javascript|js)\s*\n(?:\/\/\s*(src\/[^\n]+\.(?:tsx?|jsx?|js))[^\n]*\n)([\s\S]*?)```/gi,
                type: 'typescript-path'
            }
        ];

        patterns.forEach(({regex, type}, patternIndex) => {
            let match;
            while ((match = regex.exec(content)) !== null) {
                console.log(`📁 PATTERN ${patternIndex} (${type}) MATCH:`, {
                    fullMatch: match[0].substring(0, 100) + '...',
                    filename: match[1],
                    contentPreview: match[2]?.substring(0, 100) + '...'
                });

                let filename = match[1]?.trim();
                let fileContent = match[2]?.trim();

                // If no filename in capture group 1, try to extract from the content
                if (!filename || filename.length === 0) {
                    // Look for filename in the first few lines of content
                    const lines = fileContent?.split('\n').slice(0, 5) || [];
                    for (const line of lines) {
                        const fileMatch = line.match(/(?:\/\/|\/\*)\s*(?:Complete file:|File:|src\/[^\s*]+\.(?:tsx?|jsx?|js|css|mo))/i);
                        if (fileMatch) {
                            const extracted = line.match(/(?:src\/[^\s*]+|[^\s*]+\.(?:tsx?|jsx?|js|css|mo))/i);
                            if (extracted) {
                                filename = extracted[0].replace(/\*\/.*$/, '').trim();
                                break;
                            }
                        }
                    }
                }

                if (!filename && fileContent) {
                    // Try to infer filename from imports or content
                    const importMatch = fileContent.match(/import.*from\s+['"]\.\/([^'"]+)['"]/);
                    if (importMatch) {
                        filename = `src/frontend/src/${importMatch[1]}`;
                        if (!filename.includes('.')) {
                            filename += type === 'typescript' ? '.tsx' : '.js';
                        }
                    }
                }

                if (filename && fileContent && fileContent.length > 50) {
                    // Clean up filename
                    filename = filename.replace(/^.*\/\/\s*/, '');
                    filename = filename.replace(/^.*\/\*\s*/, '');
                    filename = filename.replace(/\s*\*\/.*$/, '');
                    filename = filename.replace(/^Complete file:\s*/i, '');
                    filename = filename.trim();

                    // Ensure proper file extension
                    if (!filename.includes('.')) {
                        if (type === 'css') filename += '.css';
                        else if (type === 'motoko') filename += '.mo';
                        else filename += '.tsx';
                    }

                    // Ensure proper path structure
                    if (!filename.includes('/')) {
                        const ext = filename.substring(filename.lastIndexOf('.')).toLowerCase();
                        if (ext === '.mo') {
                            filename = `src/backend/src/${filename}`;
                        } else if (['.css'].includes(ext)) {
                            filename = `src/frontend/src/${filename}`;
                        } else {
                            filename = `src/frontend/src/${filename}`;
                        }
                    }

                    // Clean up content - remove any leading comments about the file
                    fileContent = fileContent.replace(/^(?:\/\/|\/\*).*?(?:Complete file|File):.*?\n/i, '');
                    fileContent = fileContent.replace(/^\/\*[\s\S]*?\*\/\s*\n/, '');
                    fileContent = fileContent.trim();

                    console.log(`✅ EXTRACTED FILE:`, {
                        filename: filename,
                        contentLength: fileContent.length,
                        contentPreview: fileContent.substring(0, 150) + '...'
                    });

                    files[filename] = fileContent;
                }
            }
        });

        console.log(`🎯 FINAL DETECTED FILES:`, Object.keys(files));
        return files;
    };

    const extractCodeBlocks = (content: string): Array<{ language: string; code: string }> => {
        const codeBlockRegex = /```(\w*)\s*\n([\s\S]*?)```/g;
        const blocks: Array<{ language: string; code: string }> = [];
        let match;

        while ((match = codeBlockRegex.exec(content)) !== null) {
            const language = match[1] || 'text';
            const code = match[2].trim();

            if (code && code.length > 10) {
                blocks.push({
                    language: language,
                    code: code
                });
            }
        }

        return blocks;
    };

    const renderMessage = (message: ChatMessage) => {
        if (message.role === 'user') {
            return (
                <div key={message.id} style={{
                    display: 'flex', flexDirection: 'column', gap: '4px',
                    alignSelf: 'flex-end', maxWidth: '85%'
                }}>
                    <div style={{fontSize: '11px', color: '#8B4513', fontWeight: '500', marginLeft: 'auto'}}>
                        You
                    </div>
                    <div style={{
                        padding: '12px', borderRadius: '8px', backgroundColor: '#5C4033',
                        color: '#FFF5EE', fontSize: '14px', lineHeight: '1.4',
                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                    }}>
                        {message.content}
                    </div>
                </div>
            );
        }

        // FIXED: Use the enhanced detectCompleteFiles function
        const backendFiles = message.extractedFiles || {};
        const detectedFiles = detectCompleteFiles(message.content);
        const allFiles = {...detectedFiles, ...backendFiles};
        const codeBlocks = extractCodeBlocks(message.content);

        const hasFiles = Object.keys(allFiles).length > 0;
        const hasSingleCode = codeBlocks.length > 0 && !hasFiles;
        const showApplyButton = hasFiles || hasSingleCode;

        console.log(`🎯 MESSAGE ANALYSIS:`, {
            messageId: message.id,
            backendFiles: Object.keys(backendFiles),
            detectedFiles: Object.keys(detectedFiles),
            allFiles: Object.keys(allFiles),
            hasFiles,
            hasSingleCode,
            showApplyButton
        });

        return (
            <div key={message.id} style={{
                display: 'flex', flexDirection: 'column', gap: '4px',
                alignSelf: 'flex-start', maxWidth: '85%'
            }}>
                <div style={{fontSize: '11px', color: '#8B4513', fontWeight: '500'}}>
                    AI Assistant
                </div>

                <div style={{
                    padding: '12px', borderRadius: '8px', backgroundColor: '#f3e5d8',
                    color: '#5C4033', fontSize: '14px', lineHeight: '1.4',
                    whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                }}>
                    {message.content}
                </div>

                {showApplyButton && (
                    <div style={{
                        backgroundColor: 'rgba(75, 83, 32, 0.1)',
                        border: '1px solid #4B5320',
                        borderRadius: '6px',
                        padding: '12px',
                        marginTop: '8px'
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '8px'
                        }}>
                            <div>
                                <div style={{
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    color: '#4B5320',
                                    marginBottom: '2px'
                                }}>
                                    🔧 {hasFiles ? `${Object.keys(allFiles).length} Files Ready` : 'Code Block Ready'}
                                </div>
                                <div style={{
                                    fontSize: '10px',
                                    color: '#8B4513'
                                }}>
                                    {hasFiles ? `Complete files detected: ${Object.keys(allFiles).join(', ')}` : 'Apply to current file'}
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    if (hasFiles) {
                                        applyFileChanges(allFiles);
                                    } else if (codeBlocks.length > 0) {
                                        // Apply single code block to current file
                                        let cleanContent = codeBlocks[0].code;
                                        cleanContent = cleanContent.replace(/```[\w]*\n?/g, '');
                                        cleanContent = cleanContent.replace(/```\n?/g, '');
                                        cleanContent = cleanContent.replace(/^\/\/ [^\n]*\n/, '');
                                        cleanContent = cleanContent.replace(/^\/\* [^\n]* \*\/\n/, '');
                                        cleanContent = cleanContent.trim();

                                        onUpdateContent(cleanContent);
                                        onSuccess('Code applied to current file');
                                    }
                                }}
                                disabled={isApplyingChanges}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: isApplyingChanges ? '#9B5523' : '#4B5320',
                                    color: '#FFFFFF',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: isApplyingChanges ? 'not-allowed' : 'pointer',
                                    fontSize: '12px',
                                    fontWeight: '600',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                }}
                            >
                                {isApplyingChanges ? '⏳ Applying...' : (hasFiles ? '🔧 Apply All Files' : '📝 Apply Code')}
                            </button>
                        </div>

                        {hasFiles && (
                            <div style={{
                                fontSize: '10px',
                                color: '#8B4513',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px'
                            }}>
                                {Object.keys(allFiles).map(fileName => (
                                    <span key={fileName} style={{
                                        backgroundColor: 'rgba(75, 83, 32, 0.2)',
                                        padding: '2px 6px',
                                        borderRadius: '3px',
                                        border: '1px solid #4B5320'
                                    }}>
                                        {fileName.split('/').pop()}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    const getContextSummary = () => {
        const enabledRules = aiRules.filter(r => r.enabled).length;
        const activeDocs = docSources.filter(d => d.status === 'active').length;
        const connectedMcp = mcpServers.filter(m => m.status === 'connected').length;
        const selectedFileCount = selectedFiles.length;

        return {
            enabledRules,
            activeDocs,
            connectedMcp,
            selectedFileCount,
            hasEnhancedContext: enabledRules > 0 || activeDocs > 0 || connectedMcp > 0
        };
    };

    const contextSummary = getContextSummary();

    return (
        <div style={{
            width: isExpanded ? '400px' : '40px',
            height: '100%',
            backgroundColor: '#fdf6e3',
            borderLeft: '1px solid #8B4513',
            display: 'flex',
            flexDirection: 'column',
            transition: 'width 0.3s ease',
            position: 'relative',
            overflow: 'hidden'
        }}>
            {renderApplyProgress()}

            <div style={{
                position: 'absolute',
                right: isExpanded ? '4px' : 'auto',
                left: isExpanded ? 'auto' : '50%',
                top: isExpanded ? '4px' : '50%',
                transform: isExpanded ? 'none' : 'translate(-50%, -50%)',
                zIndex: 10
            }}>
                <button
                    onClick={onToggle}
                    style={{
                        width: '32px',
                        height: '32px',
                        backgroundColor: '#8B4513',
                        color: '#FFF5EE',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '14px',
                        fontWeight: 'bold'
                    }}
                    title={isExpanded ? 'Collapse Chat' : 'Open Chat'}
                >
                    {isExpanded ? '✕' : '💬'}
                </button>
            </div>

            {isExpanded && (
                <>
                    <div style={{
                        padding: '16px',
                        borderBottom: '1px solid #8B4513',
                        backgroundColor: '#f3e5d8'
                    }}>
                        <h3 style={{
                            margin: 0,
                            fontSize: '16px',
                            fontWeight: 'bold',
                            color: '#5C4033'
                        }}>
                            Enhanced AI Assistant
                        </h3>
                        <p style={{
                            margin: '4px 0 0 0',
                            fontSize: '12px',
                            color: '#8B4513'
                        }}>
                            Context-aware with rules, docs, and MCP integration
                        </p>

                        {/* Enhanced Context Status */}
                        {contextSummary.hasEnhancedContext && (
                            <div style={{
                                marginTop: '8px',
                                padding: '8px',
                                backgroundColor: '#e8f5e9',
                                border: '1px solid #4caf50',
                                borderRadius: '4px',
                                fontSize: '11px',
                                color: '#2e7d32'
                            }}>
                                <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px'}}>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2">
                                        <path d="M9 12l2 2 4-4"/>
                                        <circle cx="12" cy="12" r="10"/>
                                    </svg>
                                    <span style={{fontWeight: '600'}}>Enhanced Context Active</span>
                                </div>
                                <div style={{display: 'grid', gap: '2px', marginLeft: '18px'}}>
                                    {contextSummary.enabledRules > 0 && (
                                        <div>• {contextSummary.enabledRules} AI rules enforced</div>
                                    )}
                                    {contextSummary.activeDocs > 0 && (
                                        <div>• {contextSummary.activeDocs} documentation sources</div>
                                    )}
                                    {contextSummary.connectedMcp > 0 && (
                                        <div>• {contextSummary.connectedMcp} MCP servers connected</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {messages.length > 0 && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                paddingTop: '8px',
                                marginTop: '8px',
                                borderTop: '1px solid rgba(139, 69, 19, 0.2)'
                            }}>
                                <button
                                    onClick={clearConversation}
                                    style={{
                                        padding: '6px 12px',
                                        backgroundColor: 'transparent',
                                        border: '1px solid #8B4513',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontSize: '11px',
                                        color: '#8B4513'
                                    }}
                                >
                                    🗑️ Clear Conversation
                                </button>
                            </div>
                        )}
                    </div>

                    <div style={{
                        padding: '12px 16px',
                        backgroundColor: 'rgba(139, 69, 19, 0.05)',
                        borderBottom: '1px solid rgba(139, 69, 19, 0.1)',
                        position: 'relative'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '8px'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                fontSize: '12px',
                                color: '#5C4033'
                            }}>
                                <span style={{fontWeight: '600'}}>Files:</span>
                                <button
                                    onClick={() => setShowFileSelector(!showFileSelector)}
                                    style={{
                                        background: 'none',
                                        border: '1px solid #8B4513',
                                        borderRadius: '4px',
                                        padding: '4px 8px',
                                        fontSize: '11px',
                                        color: '#5C4033',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                    }}
                                >
                                    {selectedFiles.length > 0 ? `${selectedFiles.length} files selected` : 'No files selected'}
                                    <span style={{fontSize: '10px'}}>{showFileSelector ? '▲' : '▼'}</span>
                                </button>
                            </div>

                            {renderNoCodeToggle()}

                            {selectedFiles.length > 0 && (
                                <span style={{
                                    fontSize: '10px',
                                    color: '#4B5320',
                                    backgroundColor: 'rgba(75, 83, 32, 0.1)',
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    fontWeight: '500'
                                }}>
                                    {selectedFiles.length} files in context
    </span>
                            )}
                        </div>

                        {renderFileSelector()}
                    </div>

                    <div
                        style={{
                            flex: 1,
                            overflowY: 'auto',
                            padding: '16px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}

                        onScroll={(e) => {
                            const target = e.target as HTMLDivElement;
                            const isAtBottom = Math.abs(target.scrollHeight - target.scrollTop - target.clientHeight) < 5;
                            setAutoScroll(isAtBottom);

                            console.log('Scroll detection:', {
                                scrollHeight: target.scrollHeight,
                                scrollTop: target.scrollTop,
                                clientHeight: target.clientHeight,
                                isAtBottom: isAtBottom
                            });
                        }}
                    >
                        {messages.length === 0 && (
                            <div style={{
                                textAlign: 'center',
                                color: '#8B4513',
                                fontSize: '14px',
                                padding: '20px',
                                fontStyle: 'italic'
                            }}>
                                Start a conversation with enhanced AI context!
                                {selectedFiles.length > 0
                                    ? ` ${selectedFiles.length} files selected for context.`
                                    : ' Select files above to include them in the conversation context.'
                                }
                            </div>
                        )}

                        {messages.map(renderMessage)}

                        {/* Fixed Stop Button */}
                        {isStreaming && (
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '4px',
                                alignSelf: 'flex-start',
                                maxWidth: '85%'
                            }}>
                                <div style={{fontSize: '11px', color: '#8B4513', fontWeight: '500'}}>
                                    AI Assistant
                                </div>
                                <div style={{
                                    padding: '12px',
                                    borderRadius: '8px',
                                    backgroundColor: '#f3e5d8',
                                    color: '#5C4033',
                                    fontSize: '14px',
                                    lineHeight: '1.4',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}>
                                    {streamingContent}
                                    <span style={{
                                        opacity: 0.7,
                                        animation: 'blink 1s infinite'
                                    }}>▋</span>
                                </div>
                            </div>
                        )}


                        {isLoading && !isStreaming && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                color: '#8B4513',
                                fontSize: '14px'
                            }}>
                                <div style={{
                                    width: '16px',
                                    height: '16px',
                                    border: '2px solid rgba(139, 69, 19, 0.3)',
                                    borderTopColor: '#8B4513',
                                    borderRadius: '50%',
                                    animation: 'spin 1s linear infinite'
                                }}/>
                                AI is analyzing with enhanced context...
                            </div>
                        )}

                        <div ref={messagesEndRef}/>
                    </div>

                    <div style={{
                        padding: '16px',
                        borderTop: '1px solid #8B4513',
                        backgroundColor: '#f3e5d8'
                    }}>
                        <div style={{
                            position: 'relative',
                            marginBottom: '8px'
                        }}>
                            <textarea
                                ref={inputRef}
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                placeholder={selectedFiles.length > 0
                                    ? `Ask about your selected files... Enhanced context enabled!`
                                    : `Ask anything... Select files above for context!`
                                }
                                disabled={isLoading}
                                style={{
                                    width: '100%',
                                    minHeight: '48px',
                                    maxHeight: '120px',
                                    padding: '14px 50px 14px 14px',
                                    border: '1px solid #8B4513',
                                    borderRadius: '8px',
                                    backgroundColor: '#FFF5EE',
                                    color: '#5C4033',
                                    fontSize: '14px',
                                    resize: 'none',
                                    outline: 'none',
                                    fontFamily: 'inherit',
                                    boxSizing: 'border-box'
                                }}
                            />
                            <button
                                onClick={() => isStreaming ? stopResponse() : sendMessage()}
                                disabled={(!inputValue.trim() && !isStreaming) || (isLoading && !isStreaming)}
                                style={{
                                    position: 'absolute',
                                    right: '8px',
                                    bottom: '12px',
                                    width: '36px',
                                    height: '36px',
                                    backgroundColor: isStreaming ? '#dc2626' : ((!inputValue.trim() || isLoading) ? '#9B5523' : '#5C4033'),
                                    color: '#FFF5EE',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '500',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                {isStreaming ? '⏹' : (isLoading ? '⏳' : '➤')}
                            </button>
                        </div>

                        <div style={{
                            fontSize: '11px',
                            color: '#8B4513',
                            textAlign: 'center',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '4px',
                            flexWrap: 'wrap'
                        }}>
                            <span>Press Enter to send</span>
                            <span>•</span>
                            <span>Auto file extraction</span>
                            <span>•</span>
                            <span>
                                {selectedFiles.length > 0
                                    ? `${selectedFiles.length} files`
                                    : 'No files'
                                }
                            </span>
                            {contextSummary.hasEnhancedContext && (
                                <>
                                    <span>•</span>
                                    <span style={{color: '#4caf50', fontWeight: '500'}}>
                                        Enhanced context active
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                </>
            )}

            <style>
                {`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                @keyframes blink {
                    0%, 50% { opacity: 1; }
                    51%, 100% { opacity: 0; }
                }
                `}
            </style>

            <style>
                {`
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                `}
            </style>
        </div>
    );
});

ChatSidebar.displayName = 'ChatSidebar';
export default ChatSidebar;